import React from 'react';
import { CloudIcon } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useTranslation } from '../../hooks/useTranslation';
import { CloudButton } from './CloudButton';
import { ClientOnly } from '../ClientOnly';

export const LoginButton: React.FC = () => {
  const { user, loading } = useAuthStore();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
    );
  }

  if (user) return null;

  return (
    <div className="bg-gradient-to-br from-white/90 to-white/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-[0_20px_40px_rgba(0,0,0,0.12)] p-4 xs:p-5 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center gap-4 xs:gap-5 md:gap-12">
        <div className="flex items-center gap-3 xs:gap-4 md:gap-5">
          <div className="relative">
            <div className="w-10 h-10 xs:w-11 xs:h-11 md:w-12 md:h-12 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.06)]">
              <svg viewBox="0 0 24 24" className="w-5 h-5 xs:w-5.5 xs:h-5.5 md:w-6 md:h-6 text-gray-600">
                <path fill="currentColor" d="M20 8H4V6c0-1.1.9-2 2-2h12c1.1 0 2 .9 2 2v2Zm0 2v8c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2v-8h16ZM7 7c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1Zm3 0c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1Z"/>
              </svg>
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 xs:w-4.5 xs:h-4.5 md:w-5 md:h-5 rounded-full bg-amber-50 border-2 border-white flex items-center justify-center">
              <div className="text-amber-500 text-[8px] xs:text-[9px] md:text-[10px] font-bold">!</div>
            </div>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 mb-0.5 text-sm xs:text-base">{t('auth.browserOnly')}</h3>
            <p className="text-gray-500 text-xs xs:text-sm">{t('auth.clearCacheWarning')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 xs:gap-4 md:gap-5">
          <div className="relative">
            <div className="w-10 h-10 xs:w-11 xs:h-11 md:w-12 md:h-12 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.06)]">
              <CloudIcon className="w-5 h-5 xs:w-5.5 xs:h-5.5 md:w-6 md:h-6 text-primary-600" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 xs:w-4.5 xs:h-4.5 md:w-5 md:h-5 rounded-full bg-green-50 border-2 border-white flex items-center justify-center">
              <div className="w-1.5 h-1.5 xs:w-1.5 xs:h-1.5 md:w-2 md:h-2 rounded-full bg-green-500" />
            </div>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 mb-0.5 text-sm xs:text-base">{t('auth.neverLoseWork')}</h3>
            <p className="text-gray-500 text-xs xs:text-sm">{t('auth.autoSaveCloud')}</p>
          </div>
        </div>
        <div className="flex justify-center md:ml-auto">
          <CloudButton variant="default" className="" />
        </div>
      </div>
    </div>
  );
};

export const ClientOnlyLoginButton: React.FC = () => {
  return (
    <ClientOnly>
      <LoginButton />
    </ClientOnly>
  );
}; 